import React, {useCallback, useEffect, useMemo} from 'react';
import {FormProvider, useForm, useWatch} from 'react-hook-form';
import {Spinner, TextInputField} from 'evergreen-ui';
import {ValidatedTextInputField} from '../../../common';
import {formatISODateStr} from '../../../common/util/date';
import {EMAIL_VALIDATION_REGEX} from '../../../common/util/validations';
import {SPLIT_DELIMITER, splitAndRemoveEmpty} from '../../../placement/const';
import {userInputValidationRules} from '../../../usermanagementapp/domain/validationRules';
import {ModalMultiSelectionInput} from '../form-components/ModalMultiSelectionInput';
import {ModalSelectionInput} from '../form-components/ModalSelectionInput';
import {ModalTextAreaInput} from '../form-components/ModalTextAreaInput';
import {BusinessContactRow} from './domain/types';
import {formatUsPhone, typeUsPhone} from './domain/phone';
import {useBusinessFunctionOptions} from './useBusinessFunctionOptions';

/** The Status dropdown's two values. The wire carries a boolean, `inactive`. */
type ContactStatus = 'active' | 'inactive';

/**
 * The form's own shape. businessFunction is a string[] here and a semicolon-joined string on the
 * wire: ModalMultiSelectionInput writes an array into form state the first time a user touches the
 * picker, so treating it as an array throughout is the only shape that works before and after that.
 * status is the mockup's dropdown and becomes the `inactive` boolean on the way out.
 */
type ContactFormValues = {
    firstName: string;
    lastName: string;
    title: string;
    email: string;
    businessFunction: string[];
    contactPhone: string;
    status: ContactStatus;
    inactiveReason: string;
    notesForCcs: string;
};

/** What leaves the form, ready for the create and update mutations. */
export type ContactFormSubmission = {
    firstName: string;
    lastName: string;
    title: string;
    email: string;
    /** Semicolon-joined with no spaces, which is how Salesforce stores a multi-select. */
    businessFunction: string;
    contactPhone: string;
    inactive: boolean;
    inactiveReason: string;
    notesForCcs: string;
};

type Props = {
    /** The contact being edited, or null for a new one. */
    contact: BusinessContactRow | null;
    /** True while a save is in flight. Keeps the user from submitting the same contact twice. */
    saving: boolean;
    /** The backend's own message when a save failed. Empty means nothing has gone wrong. */
    errorMessage: string;
    onCancel: () => void;
    onSubmit: (values: ContactFormSubmission) => void;
};

const NAME_MAX_LENGTH = 100;
const EMAIL_MAX_LENGTH = 255;
const TITLE_MAX_LENGTH = 255;
/** inactive_reason is VARCHAR(255), and the Salesforce Inactive_Reason__c TextArea holds the same. */
const INACTIVE_REASON_MAX_LENGTH = 255;
/** Salesforce's Notes_for_CCS__c holds 4000 characters, so a longer note would be refused on save. */
const NOTES_MAX_LENGTH = 4000;

/**
 * The rule is one of the two, not both, and each field names what it wants (BHPNPP-3307): one
 * shared sentence under both fields read as a copy-paste mistake to QA. It applies to an active
 * contact only. An inactive contact is not reached any more, and its email is wiped on save.
 */
const EMAIL_MISSING_MESSAGE = 'Enter an email address.';
const PHONE_MISSING_MESSAGE = 'Enter a contact phone number.';
const PHONE_FORMAT_MESSAGE = 'Please enter a valid phone number.';

/**
 * Salesforce's own Contact.Phone rules, Is_Valid_Phone and Phone_Number_Must_Be_a_Number: the
 * shape below, and exactly ten digits with the first and fourth in 2 to 9 once the punctuation
 * Salesforce strips is gone. Salesforce strips parentheses, dashes, spaces and commas only, so a
 * dot survives and fails there; it has to fail here too. A phone refused here costs one message
 * under the field; the same phone refused by Salesforce costs a round trip and a rolled-back save
 * (BHPNPP-3308).
 */
const PHONE_SHAPE = /^\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4}$/;
const PHONE_DIGITS = /^[2-9]\d{2}[2-9]\d{6}$/;
const isSalesforcePhone = (value: string) =>
    PHONE_SHAPE.test(value) && PHONE_DIGITS.test(value.replace(/[()\- ,]/g, ''));

/**
 * The shared email rule with case ignored. People type capitals, and contacts synced from Salesforce
 * carry them; without the flag such a contact cannot be saved at all. User Management keeps the
 * shared rule as it is.
 */
const EMAIL_PATTERN = {
    value: new RegExp(EMAIL_VALIDATION_REGEX.source, 'i'),
    message: 'The field is an invalid email address',
};

const STATUS_OPTIONS = new Map<ContactStatus, string>([['active', 'Active'], ['inactive', 'Inactive']]);

/** The table header's 14px bold, above every field, the same on evergreen and house inputs. */
const LABEL_CLASS = 'text-sm font-bold';
/** Evergreen's default 32px input is cramped at 16px text. */
const INPUT_HEIGHT = 40;
const SAVE_BLUE = '#1A6DFF';
/** The mockup's chips: light gray, dark text, soft corners, a step under the 16px values. */
const CHIP_CLASS = 'bg-gray-200 text-gray-800 rounded text-sm';

const isFilled = (value: string | null | undefined) => (value ?? '').trim() !== '';

const REQUIRED_MESSAGE = 'This field is required.';

/**
 * A required rule alone accepts a string of spaces, and the backend refuses it with StringUtils
 * .isBlank. Catching it here saves the user a round trip.
 */
const notBlank = (value: string) => isFilled(value) || REQUIRED_MESSAGE;

/**
 * Salesforce joins a multi-select on a bare semicolon; the picker splits on semicolon plus space.
 * This converts a stored value into what the picker understands.
 */
const toPickerValues = (businessFunction: string | null | undefined): string[] =>
    splitAndRemoveEmpty((businessFunction ?? '').split(';').join(SPLIT_DELIMITER));

/** The reverse. Handles both sides of the field's own type shift, string before the first touch. */
const toStoredValue = (value: string[] | string): string =>
    (Array.isArray(value) ? value : splitAndRemoveEmpty(value)).join(';');

const defaultsFor = (contact: BusinessContactRow | null): ContactFormValues => ({
    firstName: contact?.firstName ?? '',
    lastName: contact?.lastName ?? '',
    title: contact?.title ?? '',
    email: contact?.email ?? '',
    businessFunction: toPickerValues(contact?.businessFunction),
    // Opens in the shape the field types, so the next save stores that shape.
    contactPhone: formatUsPhone(contact?.contactPhone ?? ''),
    status: contact?.inactive ? 'inactive' : 'active',
    inactiveReason: contact?.inactiveReason ?? '',
    notesForCcs: contact?.notesForCcs ?? '',
});

/**
 * The Contact Management form (BHPNPP-3241). Lives inside the drawer and is mounted with a key on
 * the contact's guid, so clicking another row's pencil remounts it: react-hook-form reads
 * defaultValues once, and a form handed a second contact would otherwise keep showing the first
 * contact's values under the second contact's name.
 *
 * <p>Anything typed and not saved is discarded on that swap, by design. The success toast on save is
 * what tells a user their work landed.
 */
export const ContactForm = ({contact, saving, errorMessage, onCancel, onSubmit}: Props) => {
    const {options, loading: optionsLoading} = useBusinessFunctionOptions();
    // DropdownMultiSelect re-seeds its own checkbox state whenever its options prop changes identity,
    // so a list rebuilt on every render throws away whatever the user has ticked as soon as anything
    // else re-renders this form. Both lists are therefore memoized on what the options say rather
    // than on the array they arrived in, which holds however the query hook chooses to return them.
    const optionsKey = options.map((option) => `${option.value}|${option.label}`).join(';');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const optionValues = useMemo(() => options.map((option) => option.value), [optionsKey]);
    const labelByValue = useMemo(
        // eslint-disable-next-line react-hooks/exhaustive-deps
        () => new Map(options.map((option) => [option.value, option.label])), [optionsKey]);

    const methods = useForm<ContactFormValues>({defaultValues: defaultsFor(contact)});
    const {control, getValues, setValue, clearErrors, handleSubmit} = methods;

    const inactive = useWatch({control, name: 'status'}) === 'inactive';

    // Switching Status back to Active unmounts the reason field, and at react-hook-form 7.15.4 an unmounted
    // Controller keeps its value in form state unless shouldUnregister is set. ModelMapper on the
    // backend has no null skip, so a stale reason would be written to the row and pushed to
    // Salesforce for a contact that is active.
    // Switching to Inactive turns the email-or-phone rule off. A message it left from an earlier Save
    // would otherwise keep asking for a field the save no longer needs.
    useEffect(() => {
        if (!inactive) {
            setValue('inactiveReason', '');
        } else {
            clearErrors(['email', 'contactPhone']);
        }
    }, [inactive, setValue, clearErrors]);

    // An empty picker means the list is unknown, not that the contact has no business functions:
    // Salesforce answers an empty list when its describe callout fails, and that answer is cached for
    // five minutes. The picker writes back only what it can see, so a user who so much as opens it
    // during that window would clear every function the contact carries. While the list is empty the
    // form sends back what the contact already had and the picker is held shut.
    const optionsUnknown = optionValues.length === 0;

    const submit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // status stays behind: the submission is spread straight into the GraphQL input, which has
        // no such field.
        return handleSubmit(({status, ...values}) => onSubmit({
            ...values,
            // Trimmed here rather than on each field's blur: ValidatedTextInputField spreads
            // register() after its other props, so register's own onBlur wins and a handler passed in
            // never runs.
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            title: values.title.trim(),
            email: values.email.trim(),
            contactPhone: values.contactPhone.trim(),
            inactive: status === 'inactive',
            businessFunction: optionsUnknown
                ? (contact?.businessFunction ?? '')
                : toStoredValue(values.businessFunction),
        }))(e);
    }, [handleSubmit, onSubmit, optionsUnknown, contact]);

    // The whole form waits behind one spinner rather than one field unlocking on its own later.
    // The hooks above keep running, so what was typed survives if the list ever reloads.
    if (optionsLoading) {
        return (
            <div data-testid="contact-form-loading" className="flex justify-center py-16">
                <Spinner/>
            </div>
        );
    }

    return (
        <FormProvider {...methods}>
            <form autoComplete="off" onSubmit={submit} className="portal-contact-form flex flex-col">
                <div className="px-6">
                    <ValidatedTextInputField<ContactFormValues>
                        name="firstName"
                        label="First Name"
                        placeholder="John"
                        disabled={saving}
                        maxLength={NAME_MAX_LENGTH}
                        inputWidth="100%"
                        inputHeight={INPUT_HEIGHT}
                        validationRules={{
                            required: userInputValidationRules.required,
                            maxLength: {value: NAME_MAX_LENGTH, message: `Maximum length is ${NAME_MAX_LENGTH} characters`},
                            validate: notBlank,
                        }}/>
                    <ValidatedTextInputField<ContactFormValues>
                        name="lastName"
                        label="Last Name"
                        placeholder="Doe"
                        disabled={saving}
                        maxLength={NAME_MAX_LENGTH}
                        inputWidth="100%"
                        inputHeight={INPUT_HEIGHT}
                        validationRules={{
                            required: userInputValidationRules.required,
                            maxLength: {value: NAME_MAX_LENGTH, message: `Maximum length is ${NAME_MAX_LENGTH} characters`},
                            validate: notBlank,
                        }}/>
                    <ValidatedTextInputField<ContactFormValues>
                        name="email"
                        label="Email"
                        type="email"
                        placeholder="john.doe@example.com"
                        disabled={saving}
                        maxLength={EMAIL_MAX_LENGTH}
                        inputWidth="100%"
                        inputHeight={INPUT_HEIGHT}
                        validationRules={{
                            pattern: EMAIL_PATTERN,
                            maxLength: {value: EMAIL_MAX_LENGTH, message: `Maximum length is ${EMAIL_MAX_LENGTH} characters`},
                            // deps re-runs the phone's rule too. Without it react-hook-form validates
                            // only the field that changed, so typing an email here would clear this
                            // error and leave the phone's own one sitting on the phone field.
                            deps: ['contactPhone'],
                            validate: (value: string) =>
                                getValues('status') === 'inactive'
                                || isFilled(value) || isFilled(getValues('contactPhone')) || EMAIL_MISSING_MESSAGE,
                        }}/>
                    <ValidatedTextInputField<ContactFormValues>
                        name="title"
                        label="Title"
                        placeholder="Director of Risk and Compliance"
                        disabled={saving}
                        maxLength={TITLE_MAX_LENGTH}
                        inputWidth="100%"
                        inputHeight={INPUT_HEIGHT}
                        validationRules={{
                            maxLength: {value: TITLE_MAX_LENGTH, message: `Maximum length is ${TITLE_MAX_LENGTH} characters`},
                        }}/>
                    <ModalMultiSelectionInput<ContactFormValues>
                        field="businessFunction"
                        label="Business Function(s)"
                        labelPosition="top"
                        labelClassName={LABEL_CLASS}
                        disabled={saving || optionsUnknown}
                        required={false}
                        options={optionValues}
                        showBadges
                        badgeClassName={CHIP_CLASS}
                        labelMapperFunc={(value) => labelByValue.get(value) ?? value}/>
                    {/* The picker is locked on purpose here, and the user has to be told why. */}
                    {optionsUnknown && (
                        <div className="-mt-1 mb-2 text-sm text-gray-600">
                            Business functions could not be loaded. Saved values are kept.
                        </div>
                    )}
                    <ValidatedTextInputField<ContactFormValues>
                        name="contactPhone"
                        // Ten digits at most and nothing else, shaped as the user types.
                        shapeValue={typeUsPhone}
                        label="Contact Phone"
                        placeholder="(855) 223-7123"
                        disabled={saving}
                        inputWidth="100%"
                        inputHeight={INPUT_HEIGHT}
                        validationRules={{
                            deps: ['email'],
                            // Keys run in this order and stop at the first failure, so a blank phone
                            // reads one sentence, not two. Trimmed because submit trims: a pasted
                            // phone with spaces around it saves the same as a typed one.
                            validate: {
                                reachable: (value: string) =>
                                    getValues('status') === 'inactive'
                                    || isFilled(value) || isFilled(getValues('email')) || PHONE_MISSING_MESSAGE,
                                format: (value: string) =>
                                    !isFilled(value) || isSalesforcePhone(value.trim()) || PHONE_FORMAT_MESSAGE,
                            },
                        }}/>
                    <ModalSelectionInput<ContactFormValues>
                        field="status"
                        label="Status"
                        labelPosition="top"
                        labelClassName={LABEL_CLASS}
                        disabled={saving}
                        required={false}
                        options={STATUS_OPTIONS}/>
                    {/* Unmounted rather than hidden. A Controller inside a hidden div stays
                        registered and keeps validating. Required while shown: Salesforce refuses an
                        inactive contact with no reason. */}
                    {inactive && (
                        <ModalTextAreaInput<ContactFormValues>
                            field="inactiveReason"
                            label="Inactive Reason"
                            labelPosition="top"
                            labelClassName={LABEL_CLASS}
                            placeholder="Why is this contact inactive?"
                            disabled={saving}
                            requiredMessage="Enter a reason for inactivating this contact."
                            rowsNum={3}
                            maxLength={INACTIVE_REASON_MAX_LENGTH}
                            showCharacterCount/>
                    )}
                    <ModalTextAreaInput<ContactFormValues>
                        field="notesForCcs"
                        label="Notes for CCS"
                        labelPosition="top"
                        labelClassName={LABEL_CLASS}
                        placeholder="Anything Catalight should know about this contact"
                        disabled={saving}
                        required={false}
                        rowsNum={4}
                        maxLength={NOTES_MAX_LENGTH}
                        showCharacterCount/>
                    {/* Read-only, and only on an existing contact: the stamp is the backend's, set
                        on save. A new contact has none yet. */}
                    {contact && (
                        <TextInputField
                            label="Last Updated"
                            value={formatISODateStr(contact.sfLastModifiedDate, 'MM/dd/yyyy hh:mm a')}
                            disabled
                            inputWidth="100%"
                            inputHeight={INPUT_HEIGHT}/>
                    )}
                </div>
                {/* Inline rather than a toast: a failed save keeps the drawer open, and the reason
                    has to stay on screen next to the form the user is about to fix. */}
                {errorMessage !== '' && (
                    <div role="alert" className="px-6 pb-2 text-red-700 text-sm">{errorMessage}</div>
                )}
                {/* The mockup's pair: a solid Save Changes with an outline Cancel under it, both full
                    width. */}
                <div className="flex flex-col px-6 pt-2 pb-6">
                    <button type="submit"
                            disabled={saving}
                            className="w-full h-10 rounded font-semibold text-white focus:outline-none disabled:opacity-50"
                            style={{background: SAVE_BLUE}}>
                        Save Changes
                    </button>
                    <button type="button"
                            onClick={onCancel}
                            disabled={saving}
                            className="w-full h-10 mt-3 rounded font-semibold bg-white border focus:outline-none disabled:opacity-50"
                            style={{color: SAVE_BLUE, borderColor: SAVE_BLUE}}>
                        Cancel
                    </button>
                </div>
            </form>
        </FormProvider>
    );
};
